import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { firebase } from '@/shared/firebase';
import authApi from '@/shared/auth-api';
import type { AdminUser } from '@/contracts/admin-user';

/*
 * `AdminUser` moved to `src/contracts/admin-user.ts` under T68: `src/shared/auth-api.ts` needs it
 * and `shared` may not import the legacy layer this file sits in. Re-exported here so the hook's
 * public surface is unchanged for anything that imported it from this module.
 */
export type { AdminUser };

interface AuthContextType {
    user: AdminUser | null;
    loading: boolean;
    isAuthenticated: boolean;
    authError: string | null;
    signInWithGoogle: () => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<AdminUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        authApi
            .me()
            .then(userData => {
                setUser(userData);
                setAuthError(null);
            })
            .catch(() => {
                setUser(null);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const signInWithGoogle = async () => {
        setAuthError(null);
        try {
            const provider = new firebase.auth.GoogleAuthProvider();
            const result = await firebase.auth().signInWithPopup(provider);
            const token = await result.user!.getIdToken();

            const data = await authApi.exchangeFirebaseToken(token);
            setUser(data.user);
        } catch (err: any) {
            // NEX-1277 — el usuario cerró el popup de Google sin elegir cuenta.
            // No es un error real: dejamos authError en null para que quede en
            // un estado desde el que se puede reintentar, sin mensaje falso.
            const isPopupCancelled =
                err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request';

            /*
             * F-007. Firebase reports a broken environment through `err.code`, and this branch only
             * ever read `err.message` — so `auth/invalid-api-key` ("API key not valid") matched none
             * of the 403 strings below and fell through to "the API is down". An operator then
             * chased a backend that was never involved, and F-116/F-117 both surfaced this way.
             *
             * Matched by shape rather than by list. Every Firebase code is `auth/<something>`, and
             * the ones this branch already handles above are the two popup cancellations, so any
             * remaining `auth/` code is the SDK failing before a request was ever made — which is
             * a frontend configuration fault whatever its exact name. Enumerating
             * `invalid-api-key`, `api-key-not-valid`, `invalid-app-credential`,
             * `unauthorized-domain`, `configuration-not-found` would go stale the first time
             * Firebase added a sixth.
             */
            const code: string = err?.code ?? '';
            const isFirebaseConfig = code.startsWith('auth/');

            if (isPopupCancelled) {
                setAuthError(null);
            } else if (isFirebaseConfig) {
                setAuthError(
                    'La configuración de Firebase de este entorno es inválida. ' +
                        'No es una falla de la API — avisá a IT.',
                );
            } else {
                const message = err?.message ?? err?.data?.message ?? '';
                if (
                    message.includes('403') ||
                    message.includes('not authorized') ||
                    message.includes('Account not authorized')
                ) {
                    setAuthError('Tu cuenta no tiene permiso para acceder al backoffice. Contactá a IT.');
                } else {
                    setAuthError('No se pudo conectar con el servidor. Verificá que la API esté corriendo.');
                }
            }
            await firebase.auth().signOut();
            setUser(null);
            throw err;
        }
    };

    const logout = async () => {
        try {
            await authApi.logout();
        } catch { /* ignore */ }
        await firebase.auth().signOut();
        setUser(null);
        setAuthError(null);
        navigate('/login');
    };

    return (
        <AuthContext.Provider value={{ user, loading, isAuthenticated: !!user, authError, signInWithGoogle, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
